import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { isTokenExpired } from '@/utils/tokenUtils';

const ImageUpload = ({ value, onChange, label = "Görsel" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const fileInputRef = useRef(null);

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // More aggressive: Max dimensions 1200x1200
          const maxWidth = 1200;
          const maxHeight = 1200;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          
          // Better quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // More aggressive compression: quality 0.6
          canvas.toBlob(
            (blob) => {
              // If still too large, compress even more
              if (blob.size > 500000) { // 500KB
                canvas.toBlob(
                  (smallerBlob) => {
                    resolve(new File([smallerBlob], file.name, { type: 'image/jpeg' }));
                  },
                  'image/jpeg',
                  0.4 // Very aggressive
                );
              } else {
                resolve(new File([blob], file.name, { type: 'image/jpeg' }));
              }
            },
            'image/jpeg',
            0.6
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check token before upload
    const token = localStorage.getItem('admin_token');
    if (!token || isTokenExpired(token, 10)) {
      toast.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.', {
        duration: 5000,
        action: {
          label: 'Giriş Yap',
          onClick: () => window.location.href = '/admin/login'
        }
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin');
      return;
    }

    // Validate file size (10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan küçük olmalı');
      return;
    }

    setUploading(true);
    
    try {
      let fileToUpload = file;
      
      // SVG files don't need compression
      if (file.type !== 'image/svg+xml') {
        // Compress non-SVG images
        toast.info('Görsel optimize ediliyor...');
        fileToUpload = await compressImage(file);
      }
      
      // Show preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(fileToUpload);

      // Upload file
      const token = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await axios.post(
        `${backendUrl}/api/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          maxBodyLength: Infinity,
          maxContentLength: Infinity
        }
      );

      if (response.data.success) {
        const imageUrl = response.data.url;
        setPreview(imageUrl);
        onChange(imageUrl);
        toast.success('Görsel başarıyla yüklendi!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Görsel yüklenemedi';
      toast.error(errorMsg);
      setPreview(value || '');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div className="flex items-start gap-4">
        {/* Preview */}
        {preview && (
          <div className="relative group">
            <img
              src={preview.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${preview}` : preview}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Upload Button */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id={`file-upload-${label}`}
          />
          
          <label
            htmlFor={`file-upload-${label}`}
            className={`
              flex items-center justify-center gap-2 px-4 py-3 
              border-2 border-dashed rounded-lg cursor-pointer
              transition-all hover:border-blue-500 hover:bg-blue-50
              ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
              ${preview ? 'border-gray-300' : 'border-gray-400'}
            `}
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-600">Yükleniyor...</span>
              </>
            ) : preview ? (
              <>
                <Upload className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-600">Görseli Değiştir</span>
              </>
            ) : (
              <>
                <ImageIcon className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-600">Görsel Seç</span>
              </>
            )}
          </label>
          
          <p className="mt-1 text-xs text-gray-500">
            PNG, JPG, GIF, WebP (Max 10MB)
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
